import { portAmounts, tubings } from '~lib/common/config/iv';
import { infusionBagVolumes } from '~lib/common/config/medication';

function validateMedsOnAtStart(caseConf) {
  const errors = [];
  const initMeds = caseConf.initialMedications || [];
  const caseFluids = caseConf.fluids || [];
  const caseDosageForms = caseConf.dosageForms || [];

  // Bag count
  if (initMeds.length > caseConf.iv.bagCount) {
    errors.push(
      medsOnAtStartError('On at Start: Number of bags is more than available', initMeds[0].id, 'bagCount')
    );
  }

  for (const initMed of initMeds) {
    const bag = infusionBagVolumes.find(bv => bv.key === initMed.volume);
    const bagVolumeML = bag ? bag.volumeML : 0;

    if (initMed.treatmentType === 'FLUID') {
      const caseFluid = caseFluids.find(f => f.key === initMed.medOrFluidId);
      // Fluid enabled
      if (!caseFluid) {
        errors.push(
          medsOnAtStartError('On at Start: Fluid not enabled', initMed.id, 'fluidEnabled')
        );
      } else {
        // Bag volume enabled
        if (!caseFluid.infusionBagVolumes.includes(initMed.volume)) {
          errors.push(
            medsOnAtStartError('On at Start: Volume not enabled', initMed.id, 'volumeEnabled')
          );
        }
      }

    } else {
      // Medication enabled
      const dosageForm = caseDosageForms.find(df => df.medicationId === initMed.medOrFluidId);
      if (!dosageForm) {
        errors.push(
          medsOnAtStartError('On at Start: Medication not enabled', initMed.id, 'medEnabled')
        );
      } else {
        const infusionRoute = dosageForm.enabledRoutes.find(route => route.key === 'IV_IO_INFUSION');
        if (!infusionRoute) {
          errors.push(
            medsOnAtStartError('On at Start: Infusion route not enabled', initMed.id, 'routeEnabled')
          );
        } else {
          // Bag volume enabled
          if (!infusionRoute.options.infusionBagVolumes ||
              !infusionRoute.options.infusionBagVolumes.includes(initMed.volume)) {
            errors.push(
              medsOnAtStartError('On at Start: Volume not enabled', initMed.id, 'volumeEnabled')
            );
          }
        }
      }
    }

    // Need either dripset or IV pump
    const piggyBack = initMed.connection && initMed.connection.type === 'MEDICATION_OR_FLUID';
    if (!piggyBack && !initMed.isDropSetEnabled && !initMed.isIvPumpEnabled) {
      errors.push(
        medsOnAtStartError('On at Start: Either drip set or IV pump must be enabled', initMed.id, 'dripSetOrPump')
      );
    }

    // Drip set
    if (initMed.isDropSetEnabled && caseConf.iv.enabled) {
      if (!caseConf.iv.dropSet.enabled) {
        // IV drip set enabled
        errors.push(
          medsOnAtStartError('On at Start: IV drip set not enabled', initMed.id, 'dripSetEnabled')
        );
      }
      else if (!caseConf.iv.dropSet.dripSet.includes(initMed.dripSet)) {
        // IV drip set selected
        errors.push(
          medsOnAtStartError('On at Start: Drip set not selected', initMed.id, 'dripSetSelected')
        );
      }

      if (!initMed.rate) {
        // IV drip set rate
        errors.push(
          medsOnAtStartError('On at Start: Rate not selected', initMed.id, 'dripSetRate')
        );
      } else if (initMed.rate < caseConf.iv.dropSet.minGTTM || initMed.rate > caseConf.iv.dropSet.maxGTTM) {
        errors.push(
          medsOnAtStartError('On at Start: Rate out of range', initMed.id, 'dripSetRateRange')
        );
      }
    }

    // Pump
    if (initMed.isIvPumpEnabled && caseConf.iv.enabled) {
      if (!caseConf.iv.pump.enabled) {
        // IV pump enabled
        errors.push(
          medsOnAtStartError('On at Start: IV pump not enabled', initMed.id, 'isPumpEnabled')
        );
      }

      // Primary
      if (initMed.ivPumpPrimary) {
        if (initMed.ivPumpPrimary.vtbi > bagVolumeML) {
          // IV pump vtbi
          errors.push(
            medsOnAtStartError('On at Start: VTBI exceeds bag volume', initMed.id, 'pumpVtbi', true)
          );
        }

        if (!initMed.ivPumpPrimary.rate) {
          // IV pump rate
          errors.push(
            medsOnAtStartError('On at Start: Rate not valid', initMed.id, 'pumpRate', true)
          );
        }
        const minutes = (initMed.ivPumpPrimary.hours * 60) + initMed.ivPumpPrimary.minutes;
        if (!minutes) {
          // IV pump time
          errors.push(
            medsOnAtStartError('On at Start: Time not valid', initMed.id, 'pumpTime', true)
          );
        }
      } else {
        // IV pump function
        errors.push(
          medsOnAtStartError('On at Start: Pump function not selected', initMed.id, 'pumpFunction', true)
        );
      }

      // Secondary
      if (initMed.isIvPumpSecondary) {
        if (initMed.ivPumpSecondary) {
          if (initMed.ivPumpSecondary.vtbi > bagVolumeML) {
            // IV pump vtbi
            errors.push(
              medsOnAtStartError('On at Start: VTBI exceeds bag volume', initMed.id, 'pumpVtbi', false)
            );
          }

          if (!initMed.ivPumpSecondary.rate) {
            // IV pump rate
            errors.push(
              medsOnAtStartError('On at Start: Rate not valid', initMed.id, 'pumpRate', false)
            );
          }
          const minutes = (initMed.ivPumpSecondary.hours * 60) + initMed.ivPumpSecondary.minutes;
          if (!minutes) {
            // IV pump time
            errors.push(
              medsOnAtStartError('On at Start: Time not valid', initMed.id, 'pumpTime', false)
            );
          }
        } else {
          // IV pump function
          errors.push(
            medsOnAtStartError('On at Start: Pump function not selected', initMed.id, 'pumpFunction', false)
          );
        }
      }
    }

    // Connection
    if (initMed.connection) {
      if (initMed.connection.type === 'IV_SITE') {
        const initIvId = initMed.connection.id;
        const initialIv = caseConf.initialIvs ? caseConf.initialIvs.find(initIv => initIv.id === initIvId) : null;
        if (!initialIv) {
          // Initial IV enabled
          errors.push(
            medsOnAtStartError('On at Start: Initial IV site not found', initMed.id, 'initialIvSiteEnabled')
          );
        } else {
          const portAmount = portAmounts.find(pa => pa.key === initialIv.portAmount);
          if (portAmount) {
            let availablePorts = portAmount.categoryPorts ? portAmount.categoryPorts[initialIv.categoryType] : null;
            if (!availablePorts) {
              availablePorts = portAmount.defaultPorts;
            }
            let secondPeripheralUnavailable = false;
            if (initialIv.portAmount === 'TWO_PORTS' && initialIv.categoryType === 'PERIPHERAL') {
              const peripheralType = caseConf.iv.types.find(ivType => ivType.type === 'PERIPHERAL');
              if (peripheralType) {
                if (!peripheralType.ports.includes('TWO_PORTS') && initMed.connection.port === 'PORT_2') {
                  secondPeripheralUnavailable = true;
                }
              }
            }
            if ((availablePorts && !availablePorts.includes(initMed.connection.port)) ||
                secondPeripheralUnavailable) {
              // Port selected
              errors.push(
                medsOnAtStartError('On at Start: Port not enabled', initMed.id, 'initialIvPortEnabled')
              );
            }
          }
        }
      } else if (initMed.connection.type === 'MEDICATION_OR_FLUID') {
        const otherInitialMed = initMeds.find(im => im.id === initMed.connection.id);
        if (!otherInitialMed) {
          // Med enabled
          errors.push(
            medsOnAtStartError('On at Start: Connection medication not enabled', initMed.id, 'connectionMed')
          );
        }
      }
    } else {
      // No connection
      errors.push(
        medsOnAtStartError('On at Start: Bag must be connected to something', initMed.id, 'bagConnected')
      );
    }

    // Bag finishes before start of case
    if (initMed.isAlreadyRunning) {
      const runningMinutes =
        initMed.runningForHour ? (initMed.runningForHour * 60) : 0 +
        initMed.runningForMinutes ? initMed.runningForMinutes : 0;
      if (runningMinutes) {
        let duration;
        if (initMed.isDropSetEnabled) {
          const tubing = tubings.find(t => t.key === initMed.dripSet);
          const gttPerMl = tubing ? tubing.gttPerMl : 0;
          const gttsPerMin = initMed.rate;
          duration = gttsPerMin ? gttPerMl / gttsPerMin * bagVolumeML : 0;
        } else if (initMed.isIvPumpEnabled && initMed.ivPumpPrimary) {
          if (initMed.isIvPumpSecondary && initMed.isIvPumpSecondaryActive) {
            duration = (initMed.ivPumpSecondary.hours * 60) + initMed.ivPumpSecondary.minutes;
          } else {
            duration = (initMed.ivPumpPrimary.hours * 60) + initMed.ivPumpPrimary.minutes;
          }
        }
        if (duration && duration <= runningMinutes) {
          errors.push(
            medsOnAtStartError('On at Start: Medication ends before start of case', initMed.id, 'priorToStart')
          );
        }
      } else {
        errors.push(
          medsOnAtStartError('On at Start: Already running time must be greater than 0', initMed.id, 'alreadyRunning')
        );
      }
    }
  }

  return {
    errors,
    warnings: [],
  };
}

function medsOnAtStartError(message, id, type, isPrimary) {
  return {
    message,
    category: 'medications',
    field: 'initialMedications',
    id,
    metadata: {
      field: 'initialMedications',
      id,
      type,
      isPrimary,
      scope: 'control',
    },
  };
}

export default validateMedsOnAtStart;
